import { inferenceGenerationEventSchema, type InferenceGenerationCallback } from '@/features/transformers-js/generation-events';
import { workerProxy } from '@/utils/worker-transport';
import { cloneChatMessages, cloneLmParameters, cloneWorkerTools } from '@/features/transformers-js/inference-input-snapshot';
import { createProductionWorkerSession } from './production-worker-session';
import { generationCaptureLimitsSchema } from './generation-capture';
import { createLoadDiagnosticLedger, type LoadDiagnosticPacket } from './load-diagnostics';
import { downloadedModelRevisionSelectionSchema, type DownloadedModelRevisionSelection } from '@/features/transformers-js/runtime/downloaded-model-revision-selection';
import {
  generationCaptureReadRequestSchema,
  generationCaptureReadResultSchema,
  generationCaptureRequestSchema,
  type GenerationCaptureRequest,
  type GenerationCaptureReadRequest,
  type GenerationCaptureReadResult,
  type GenerationCaptureClient,
  type GenerationCaptureClientLifetime,
} from './generation-capture-protocol';
import type { LmParameters, ToolCall } from '@/01-models/types';
import type { InferenceMessage } from '@/features/transformers-js/types';
import type {
  TransformersJsWorkerClient,
  WorkerToolDefinition,
  ProgressInfo,
  ModelLoadResult,
  TransformersJsProgressCallback,
  TransformersJsChunkCallback,
  TransformersJsToolCallsCallback,
} from '@/features/transformers-js/types';

function createUnavailableEnvironmentError(): Error {
  return new Error('Transformers.js worker is not available in this environment');
}

export function createTransformersJsWorkerClient(): TransformersJsWorkerClient {
  return createWorkerClientCore({ capture: undefined }).client;
}

/** Recording is opt-in for a synthetic investigation owner, never ordinary chat. */
export function createTransformersJsGenerationCaptureClient({ runId, workerEpoch, limits: rawLimits, getActiveRequest }: {
  runId: string,
  workerEpoch: number,
  limits: GenerationCaptureRequest['limits'],
  getActiveRequest: () => { runId: string; requestId: string } | undefined,
}): GenerationCaptureClient {
  const identity = generationCaptureReadRequestSchema.parse({ runId, workerEpoch });
  const limits = generationCaptureLimitsSchema.parse(rawLimits);
  const loadDiagnostics = createLoadDiagnosticLedger({ owner: identity });
  const issuedCalls: GenerationCaptureRequest['context'][] = [];
  const loadRequests: GenerationCaptureClientLifetime['loadRequests'] = [];
  const incompleteReasons = new Set<GenerationCaptureClientLifetime['incompleteReasons'][number]>();
  const core = createWorkerClientCore({
    capture: {
      loadReceiptOwner: identity,
      observeLoad({ packet }) {
        loadDiagnostics.observe({ packet });
      },
      createRequest() {
      // Read the owner once, before startup/session awaits. A tool loop may
      // issue several calls for one Provider request, each with its own ID.
        if (issuedCalls.length >= limits.maxCalls) {
          incompleteReasons.add('call-limit');
          return undefined;
        }
        try {
          const active = getActiveRequest();
          if (active === undefined) {
            incompleteReasons.add('request-unavailable');
            return undefined;
          }
          const parsed = generationCaptureRequestSchema.safeParse({
            context: { ...identity, runId: active.runId, requestId: active.requestId, generationCallId: issuedCalls.length + 1 },
            limits,
          });
          if (!parsed.success || parsed.data.context.runId !== identity.runId) {
            incompleteReasons.add('request-invalid');
            return undefined;
          }
          issuedCalls.push({ ...parsed.data.context });
          return parsed.data;
        } catch {
        // No diagnostic getter/schema failure may reject actual generation.
          incompleteReasons.add('request-invalid');
          return undefined;
        }
      },
      recordLoad({ modelId, revisionSelection }) {
        const revision = (() => {
          switch (revisionSelection.kind) {
          case 'pinned': return revisionSelection.revision;
          case 'discover-cached': return undefined;
          default: {
            const _ex: never = revisionSelection;
            throw new Error(`Unhandled revision selection: ${_ex}`);
          }
          }
        })();
        if (loadRequests.length >= limits.maxCalls) {
          incompleteReasons.add('load-limit');
        } else if (modelId.length > 256 || (revision !== undefined && revision.length > 128)) {
          incompleteReasons.add('load-identity-limit');
        } else {
        // These are requested identities, not proof of resolved revision or
        // successful Load. Do not wrap Load settlement just to record them.
          loadRequests.push({ requestedModelId: modelId, requestedRevision: revision, revisionSelection: { ...revisionSelection } });
        }
      },
    },
  });
  return {
    client: core.client,
    async takeGenerationCapture(): Promise<GenerationCaptureReadResult> {
      // The coordinator calls this after all Provider requests, not between
      // turns or before propagating a generation error. Never drain or retry.
      const parsed = generationCaptureReadResultSchema.safeParse(await core.takeGenerationCapture({ identity }));
      if (!parsed.success) {
        throw new Error('Invalid generation capture response');
      }
      switch (parsed.data.status) {
      case 'captured': {
        const { capture } = parsed.data;
        if (capture.runId !== identity.runId || capture.workerEpoch !== identity.workerEpoch
          || !generationCaptureLimitsSchema.keyof().options.every(key => capture.limits[key] === limits[key])) {
          throw new Error('Invalid generation capture response');
        }
        break;
      }
      case 'not-started': case 'invalid-context': case 'wrong-run': case 'busy': case 'already-taken': break;
      default: {
        const _ex: never = parsed.data;
        throw new Error(`Unhandled generation capture status: ${String(_ex)}`);
      }
      }
      return parsed.data;
    },
    getCaptureLifetime() {
      return {
        ...identity,
        session: core.isActive() ? 'active' as const : 'inactive' as const,
        loadDiagnostics: loadDiagnostics.snapshot({ expectedLoadCount: loadRequests.length }),
        issuedCalls: issuedCalls.map(context => ({ ...context })),
        loadRequests: loadRequests.map(request => ({ ...request, revisionSelection: request.revisionSelection === undefined ? undefined : { ...request.revisionSelection } })),
        incompleteReasons: [...incompleteReasons],
      };
    },
  };
}

function createWorkerClientCore({ capture }: {
  capture: {
    loadReceiptOwner: GenerationCaptureReadRequest,
    observeLoad({ packet }: { packet: LoadDiagnosticPacket }): void,
    createRequest(): GenerationCaptureRequest | undefined,
    recordLoad({ modelId, revisionSelection }: { modelId: string; revisionSelection: DownloadedModelRevisionSelection }): void,
  } | undefined,
}): {
  client: TransformersJsWorkerClient,
  isActive(): boolean,
  takeGenerationCapture({ identity }: { identity: GenerationCaptureReadRequest }): Promise<GenerationCaptureReadResult>,
} {
  if (typeof Worker === 'undefined') {
    const client: TransformersJsWorkerClient = {
      async loadDownloadedModel({ modelId: _modelId, revisionSelection: _revisionSelection, progressCallback: _progressCallback }) {
        throw createUnavailableEnvironmentError();
      },
      async unloadModel() {
        throw createUnavailableEnvironmentError();
      },
      async interrupt() {
        throw createUnavailableEnvironmentError();
      },
      async resetCache() {
        throw createUnavailableEnvironmentError();
      },
      async generateText({ messages: _messages, onChunk: _onChunk, onToolCalls: _onToolCalls, params: _params, tools: _tools }) {
        throw createUnavailableEnvironmentError();
      },
      async generateMessage({ messages: _messages, onEvent: _onEvent, params: _params, tools: _tools, continuationOwner: _continuationOwner }) {
        throw createUnavailableEnvironmentError();
      },
      async dispose() {
      },
    };
    return {
      client,
      isActive: () => false,
      async takeGenerationCapture() {
        throw createUnavailableEnvironmentError();
      },
    };
  }

  const worker = new Worker(
    new URL('./bootstrap.ts', import.meta.url),
    { type: 'module' },
  );

  const session = createProductionWorkerSession({
    worker,
    startupTimeoutMs: undefined,
    observeLoadDiagnostic: capture === undefined ? undefined : ({ packet }) => capture.observeLoad({ packet }),
  });
  const client: TransformersJsWorkerClient = {
    async loadDownloadedModel({ modelId, revisionSelection: rawSelection, progressCallback }: {
      modelId: string,
      revisionSelection: DownloadedModelRevisionSelection,
      progressCallback: TransformersJsProgressCallback,
    }): Promise<ModelLoadResult> {
      const revisionSelection = downloadedModelRevisionSelectionSchema.parse(rawSelection);
      capture?.recordLoad({ modelId, revisionSelection });
      return session.run({
        operation: ({ remote }) => remote.loadDownloadedModel(
          modelId, revisionSelection,
          workerProxy({
            // eslint-disable-next-line local-rules-named-args/require-named-args -- Comlink proxy callback is a positional remote boundary.
            value: (info: ProgressInfo) => {
              if (session.isActive()) {
                return progressCallback({ info });
              }
            },
          }),
          ...(capture === undefined ? [] as const : [capture.loadReceiptOwner] as const),
        ),
      });
    },
    async unloadModel(): Promise<void> {
      return session.run({ operation: ({ remote }) => remote.unloadModel() });
    },
    async interrupt(): Promise<void> {
      return session.run({ operation: ({ remote }) => remote.interrupt() });
    },
    async resetCache(): Promise<void> {
      return session.run({ operation: ({ remote }) => remote.resetCache() });
    },
    async generateText({ messages, onChunk, onToolCalls, params, tools, continuationOwner }: {
      messages: InferenceMessage[],
      onChunk: TransformersJsChunkCallback,
      onToolCalls: TransformersJsToolCallsCallback,
      params?: LmParameters,
      tools?: WorkerToolDefinition[],
      continuationOwner?: string,
    }): Promise<void> {
      // Freeze at the client call, not after asynchronous Worker startup.
      // Direct investigation clients share this boundary with the owned service.
      const accepted = {
        messages: cloneChatMessages({ messages }),
        params: cloneLmParameters({ params }),
        tools: cloneWorkerTools({ tools }),
      };
      const request = capture?.createRequest();
      let acceptingCallbacks = true;
      try {
        return await session.run({
          operation: ({ remote }) => remote.generateText(
            accepted.messages,
            workerProxy({
              // eslint-disable-next-line local-rules-named-args/require-named-args -- Comlink proxy callback is a positional remote boundary.
              value: (chunk: string) => {
                if (acceptingCallbacks && session.isActive()) {
                  return onChunk({ chunk });
                }
              },
            }),
            workerProxy({
              // eslint-disable-next-line local-rules-named-args/require-named-args -- Comlink proxy callback is a positional remote boundary.
              value: (toolCalls: ToolCall[]) => {
                if (acceptingCallbacks && session.isActive()) {
                  return onToolCalls({ toolCalls });
                }
              },
            }),
            accepted.params,
            accepted.tools,
            request,
            continuationOwner,
          ),
        });
      } finally {
        // A failed or disposed RPC cannot deliver into a later request, even
        // when its callback MessagePort still has queued messages.
        acceptingCallbacks = false;
      }
    },
    async generateMessage({ messages, onEvent, params, tools, continuationOwner }: {
      messages: InferenceMessage[],
      onEvent: InferenceGenerationCallback,
      params: LmParameters | undefined,
      tools: WorkerToolDefinition[] | undefined,
      continuationOwner: string | undefined,
    }): Promise<void> {
      const accepted = {
        messages: cloneChatMessages({ messages }),
        params: cloneLmParameters({ params }),
        tools: cloneWorkerTools({ tools }),
      };
      const request = capture?.createRequest();
      let acceptingCallbacks = true;
      try {
        await session.run({
          operation: ({ remote }) => remote.generateText(
            accepted.messages,
            workerProxy({
              value: () => {
                throw new Error('Structured generation received a legacy text callback.');
              },
            }),
            workerProxy({
              value: () => {
                throw new Error('Structured generation received a legacy tool callback.');
              },
            }),
            accepted.params, accepted.tools, request, continuationOwner,
            workerProxy({
              value: ({ event }: { event: unknown }) => {
                if (acceptingCallbacks && session.isActive()) {
                  return onEvent({ event: inferenceGenerationEventSchema.parse(event) });
                }
              },
            }),
          ),
        });
      } finally {
        acceptingCallbacks = false;
      }
    },
    async dispose(): Promise<void> {
      session.dispose();
    },
  };
  return {
    client,
    isActive: session.isActive,
    takeGenerationCapture({ identity }) {
      return session.run<GenerationCaptureReadResult>({ operation: ({ remote }) => remote.takeGenerationCapture(identity) });
    },
  };
}

// Export internal state and logic used only for testing here. Do not reference these in production logic.
// ESLint-required for TypeScript modules.
export const TEST_ONLY = {
};
